import { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { subDays } from "date-fns";

import { DateFilterType } from "@/design-system/components/DateFilter/DateFilter";
import { DateRange } from "@/analytics/DateRange";
import { allDestinationsData } from "@/destinations/destinations";
import type { AttributionModel, SourceAnalysisResponse } from "@/api/insights";
import type { ChannelContribution, TopJourney } from "@/api/customer-journeys";
import type { SourceColumnKey } from "./InsightsTableColumns";
import { downloadCsv } from "@/utils/csv";
import { formatDays } from "./CustomerJourneysTab/ChannelJourneys/formatDays";
import {
  GOOGLE_INTEGRATION_URL,
  META_INTEGRATION_URL,
  BING_INTEGRATION_URL,
  META_INTEGRATION_SHORT,
  GOOGLE_INTEGRATION_SHORT,
  BING_INTEGRATION_SHORT,
  META_INTEGRATION_KEY,
  GOOGLE_INTEGRATION_KEY,
  BING_INTEGRATION_KEY,
  DATE_FILTER_TYPE,
  MULTI_TOUCH_ATTRIBUTIONS,
} from "./constants";

// Format dollars: show 2 decimal places only if there's a fractional part
export const formatDollars = (value: number): string => {
  const hasDecimals = value % 1 !== 0;
  return value.toLocaleString(undefined, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

export const conversionsDecPlaces = 2;

export const getConversionsDecPlaces = (attributionModel: AttributionModel) =>
  attributionModel === MULTI_TOUCH_ATTRIBUTIONS.FIRST_TOUCH.key ||
  attributionModel === MULTI_TOUCH_ATTRIBUTIONS.LAST_TOUCH.key
    ? 0
    : conversionsDecPlaces;

export const toFixedSafe = (
  nbr: number | undefined,
  places: number,
): string | undefined => {
  if (nbr === undefined) return undefined;
  if (nbr === 0) return "0";
  return nbr.toLocaleString(undefined, {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  });
};

// Format Number: format numbers and/or handle null/undefined values
export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString();
};

// Animated value component that flashes when value changes
interface AnimatedValueProps {
  value: string | number | undefined;
  className?: string;
}

const platformToSlugMap: Record<string, string> = {
  meta_ads: "meta-insights",
  "meta ads": "meta-insights",
  meta: "meta-insights",
  cm360: "google-cm360-conversion-api",
};

// Returns logosquare icon for each destination
export function getDestinationIcon(platformName: string) {
  const lowerName = platformName.toLowerCase().trim();

  if (platformToSlugMap[lowerName]) {
    const slug = platformToSlugMap[lowerName];
    const destination =
      allDestinationsData[slug as keyof typeof allDestinationsData];
    return destination?.logoSquare;
  }

  const slug = lowerName.replace(/_/g, "-").replace(/ /g, "-");
  const destination =
    allDestinationsData[slug as keyof typeof allDestinationsData];
  return destination?.logoSquare;
}

export const AnimatedValue = ({ value, className }: AnimatedValueProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValueRef = useRef<string | number | undefined>(undefined);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip animation on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevValueRef.current = value;
      return;
    }

    // Animate when value changes
    if (prevValueRef.current !== value) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 600);
      prevValueRef.current = value;
      return () => clearTimeout(timer);
    }
  }, [value]);

  return (
    <span
      className={clsx(
        "transition-all duration-300",
        isAnimating && "text-textActionSelected scale-110",
        className,
      )}
    >
      {value}
    </span>
  );
};

type SkeletonCardType = {
  type?: "card" | "default";
};

export const SkeletonCard = ({ type = "card" }: SkeletonCardType) => {
  return (
    <div
      className={clsx(
        "flex w-full flex-col gap-5",
        type === "card" && "border-borderSubtle rounded-lg border p-5 shadow",
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex w-full flex-col gap-4">
          <div className="bg-fill-neutral h-6 animate-pulse rounded" />
          <div className="bg-fill-neutral h-4 animate-pulse rounded" />
          <div className="bg-fill-neutral h-20 animate-pulse rounded" />
        </div>
      </div>
    </div>
  );
};

/**
 * Converts a DateFilterType to start and end dates
 * Uses local time so dates represent start/end of day in user's timezone
 * All preset filters end at yesterday to ensure full day reporting
 * (dbt pipeline runs at 12am PT, integrations sync at 8am ET for prior day)
 */
export function getDateRangeFromFilter(dateFilter: DateFilterType): {
  startDate: Date;
  endDate: Date;
} {
  const now = new Date();
  // Use yesterday as end date to ensure full day reporting
  const yesterday = subDays(now, 1);
  yesterday.setHours(0, 0, 0, 0); // Start of yesterday in local time

  let startDate: Date;
  let endDate: Date = new Date(yesterday);
  endDate.setHours(23, 59, 59, 999); // End of yesterday in local time

  switch (dateFilter.type) {
    case "custom": {
      startDate = new Date(dateFilter.start);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(dateFilter.end);
      endDate.setHours(23, 59, 59, 999);
      break;
    }
    case "yesterday":
      startDate = new Date(yesterday);
      break;
    case DATE_FILTER_TYPE.SEVEN_DAYS:
      startDate = subDays(yesterday, 6);
      startDate.setHours(0, 0, 0, 0);
      break;
    case DATE_FILTER_TYPE.THIRTY_DAYS:
      startDate = subDays(yesterday, 29);
      startDate.setHours(0, 0, 0, 0);
      break;
    case DATE_FILTER_TYPE.THREE_MONTHS:
      startDate = new Date(
        now.getFullYear(),
        now.getMonth() - 3,
        now.getDate(),
        0,
        0,
        0,
        0,
      );
      break;
    case DATE_FILTER_TYPE.SIX_MONTHS:
      startDate = new Date(
        now.getFullYear(),
        now.getMonth() - 6,
        now.getDate(),
        0,
        0,
        0,
        0,
      );
      break;
    case DATE_FILTER_TYPE.TWELVE_MONTHS:
      startDate = new Date(
        now.getFullYear() - 1,
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        0,
      );
      break;
    default:
      startDate = subDays(yesterday, 29);
      startDate.setHours(0, 0, 0, 0);
  }

  return { startDate, endDate };
}

export function formatLocalDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseLocalDateParam(value: string, endOfDay = false): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return new Date(value);
  }

  const [, year, month, day] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
}

/**
 * Converts a DateFilterType to a DateRange object for API queries
 */
export function convertFilterToDateRange(
  dateFilter: DateFilterType,
): DateRange {
  const { startDate, endDate } = getDateRangeFromFilter(dateFilter);
  return new DateRange(startDate, endDate);
}

/**
 * Handles navigation to a destination page and persists loading state
 * @param destination - The destination type ('meta' or 'google' or 'bing')
 * @param history - React Router history object
 * @param getDestinationForSource - Function to get destinations by type
 */
export function handleDestinationConnect(
  destination:
    | typeof META_INTEGRATION_SHORT
    | typeof GOOGLE_INTEGRATION_SHORT
    | typeof BING_INTEGRATION_SHORT,
  history: { push: (path: string) => void },
  getDestinationForSource?: (sourceKey: string) => any[] | null,
): void {
  const isMeta = destination === META_INTEGRATION_SHORT;
  const isGoogle = destination === GOOGLE_INTEGRATION_SHORT;
  const storageKey = isMeta
    ? "metaDestinationVisited"
    : isGoogle
      ? "googleDestinationVisited"
      : "bingDestinationVisited";
  const integrationKey = isMeta
    ? META_INTEGRATION_KEY
    : isGoogle
      ? GOOGLE_INTEGRATION_KEY
      : BING_INTEGRATION_KEY;
  const baseUrl = isMeta
    ? META_INTEGRATION_URL
    : isGoogle
      ? GOOGLE_INTEGRATION_URL
      : BING_INTEGRATION_URL;

  sessionStorage.setItem(storageKey, "true");

  // If getDestinationForSource is provided, find the first enabled destination
  if (getDestinationForSource) {
    const integrations = getDestinationForSource(integrationKey);

    // Find the first enabled integration
    const enabledIntegration = integrations?.find(
      (integration) => integration.enabled,
    );

    if (enabledIntegration) {
      history.push(`${baseUrl}/${enabledIntegration.id}`);
      return;
    } else if (integrations && integrations.length > 0) {
      // If no enabled integration but integrations exist, go to first one
      history.push(`${baseUrl}/${integrations[0].id}`);
      return;
    }
  }

  // Fallback to generic config page
  history.push(baseUrl);
}

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

const WEEKDAY_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "long",
  day: "numeric",
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
};

// Formats a date-only string ("YYYY-MM-DD") without shifting it by the
// viewer's timezone. `new Date("YYYY-MM-DD")` parses as UTC midnight, which
// toLocaleDateString would then render a day earlier for viewers west of UTC.
export const formatDateOnly = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    ...DATE_FORMAT_OPTIONS,
    timeZone: "UTC",
  });
};

export const formatWeekDayDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", WEEKDAY_FORMAT_OPTIONS);
};

export function trackInsightsEvent(
  eventName: string,
  properties: Record<string, unknown>,
) {
  if (typeof window === "undefined" || !window.freshpaint) return;

  window.freshpaint.track(eventName, properties);
}

let hasShownConversionRecommendationModal = false;

export function shouldShowConversionRecommendationModal() {
  if (hasShownConversionRecommendationModal) {
    return false;
  }

  hasShownConversionRecommendationModal = true;
  return true;
}

/**
 * Formats a DateFilterType for display in the UI
 */
export function formatDateFilterDisplay(dateFilter: DateFilterType): string {
  switch (dateFilter.type) {
    case "yesterday":
      return "Yesterday";
    case DATE_FILTER_TYPE.SEVEN_DAYS:
      return "Last 7 complete days";
    case DATE_FILTER_TYPE.THIRTY_DAYS:
      return "Last 30 complete days";
    case DATE_FILTER_TYPE.THREE_MONTHS:
      return "Last 3 complete months";
    case DATE_FILTER_TYPE.SIX_MONTHS:
      return "Last 6 complete months";
    case DATE_FILTER_TYPE.TWELVE_MONTHS:
      return "Last 12 complete months";
    case "custom": {
      const startDate = new Date(dateFilter.start);
      const endDate = new Date(dateFilter.end);
      return `${startDate.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS)} - ${endDate.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS)}`;
    }
    default:
      return "Last 30 complete days";
  }
}

function sanitizeFilePart(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getAttributionModelLabel(model: AttributionModel): string {
  const modelOption = Object.values(MULTI_TOUCH_ATTRIBUTIONS).find(
    (option) => option.key === model,
  );
  return modelOption?.label ?? model;
}

// Metric columns in table order. The Source/Campaign columns both derive from
// the always-present "label" column, so they're emitted unconditionally.
const INSIGHTS_CSV_METRIC_COLUMNS: {
  key: SourceColumnKey;
  header: string;
  getValue: (row: SourceAnalysisResponse) => string;
}[] = [
  {
    key: "impressions",
    header: "Impressions",
    getValue: (row) => formatNumber(row.impressions),
  },
  {
    key: "clicks",
    header: "Landing page clicks",
    getValue: (row) => formatNumber(row.clicks),
  },
  {
    key: "numMidFunnelConversions",
    header: "Mid-funnel conversions",
    getValue: (row) =>
      toFixedSafe(row.numMidFunnelConversions, conversionsDecPlaces) ?? "-",
  },
  {
    key: "numBottomFunnelConversions",
    header: "Low-funnel conversions",
    getValue: (row) =>
      toFixedSafe(row.numBottomFunnelConversions, conversionsDecPlaces) ?? "-",
  },
  {
    key: "customerAcquisitionCost",
    header: "CPA ($)",
    getValue: (row) =>
      row.customerAcquisitionCost && row.customerAcquisitionCost !== 0
        ? formatDollars(row.customerAcquisitionCost)
        : "-",
  },
  {
    key: "spend",
    header: "Total spend ($)",
    getValue: (row) =>
      row.spend && row.spend !== 0 ? formatDollars(row.spend) : "-",
  },
];

/**
 * Downloads the insights table as a CSV file.
 * @param visibleColumns - When provided, restricts export to only these metric columns.
 * Omit to export all columns (used for "full table" export).
 */
export function downloadInsightsTableCsv(params: {
  rows: SourceAnalysisResponse[];
  isDemoEnv: boolean;
  dateFilter: DateFilterType;
  attributionModel: AttributionModel;
  // Restricts the metric columns in the export; omit to include all of them.
  visibleColumns?: SourceColumnKey[];
}): void {
  const { rows, dateFilter, attributionModel, visibleColumns } = params;

  const metricColumns = visibleColumns
    ? INSIGHTS_CSV_METRIC_COLUMNS.filter((column) =>
        visibleColumns.includes(column.key),
      )
    : INSIGHTS_CSV_METRIC_COLUMNS;

  const csvRows = rows
    .filter((row) => !row._isChildrenToggleRow && !row._isTierRow)
    .map((row) => {
      const source = row._isChildRow ? row._parentLabel : row.label;
      const campaign = row._isChildRow ? row.label : "";

      return [
        source ?? "",
        campaign,
        ...metricColumns.map((column) => column.getValue(row)),
      ];
    });

  const header = [
    "Source",
    "Campaign",
    ...metricColumns.map((column) => column.header),
  ];

  const dateRangePart = sanitizeFilePart(formatDateFilterDisplay(dateFilter));
  const attributionModelPart = sanitizeFilePart(
    getAttributionModelLabel(attributionModel),
  );
  const filename = `insights_table_${dateRangePart}_${attributionModelPart}.csv`;

  downloadCsv({
    filename,
    header,
    rows: csvRows,
  });
}

export function downloadTopJourneysCsv(params: {
  journeys: TopJourney[];
  dateFilter: DateFilterType;
}): void {
  const { journeys, dateFilter } = params;

  const header = [
    "Journey path",
    "Length",
    "Channels",
    "Conversions",
    "Avg time to convert",
  ];

  const csvRows = journeys.map((journey) => [
    journey.path,
    journey.pathLength,
    journey.distinctChannels,
    formatNumber(journey.conversionCount),
    formatDays(journey.avgTimeToDays),
  ]);

  const dateRangePart = sanitizeFilePart(formatDateFilterDisplay(dateFilter));
  const filename = `insights_top_journeys_${dateRangePart}.csv`;

  downloadCsv({ filename, header, rows: csvRows });
}

// Mirrors AttributedCreditCell in ChannelContributionTable: credit as a whole
// percentage of the channel's total attributed credit.
function attributedCreditPct(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

export function downloadChannelContributionCsv(params: {
  channels: ChannelContribution[];
  dateFilter: DateFilterType;
}): void {
  const { channels, dateFilter } = params;

  // The table packs each touch-type credit into one "value (pct%)" cell; the CSV
  // splits them into separate numeric + percent columns so they stay sortable
  // and analyzable in a spreadsheet.
  const header = [
    "Channel",
    "Journeys",
    "Avg position",
    "Total credit",
    "First touch credit",
    "First touch %",
    "Last touch credit",
    "Last touch %",
    "Assist credit",
    "Assist %",
    "Avg time to convert",
  ];

  const csvRows = channels.map((channel) => {
    const total = channel.totalAttributedCredit;
    return [
      channel.sourceName,
      formatNumber(channel.journeysParticipated),
      formatNumber(Math.round(channel.avgPositionInJourney)),
      formatNumber(Math.round(total)),
      formatNumber(Math.round(channel.firstTouchAttributed)),
      `${attributedCreditPct(channel.firstTouchAttributed, total)}%`,
      formatNumber(Math.round(channel.lastTouchAttributed)),
      `${attributedCreditPct(channel.lastTouchAttributed, total)}%`,
      formatNumber(Math.round(channel.assistAttributed)),
      `${attributedCreditPct(channel.assistAttributed, total)}%`,
      formatDays(channel.avgDaysToConvert),
    ];
  });

  const dateRangePart = sanitizeFilePart(formatDateFilterDisplay(dateFilter));
  const filename = `insights_channel_contribution_${dateRangePart}.csv`;

  downloadCsv({ filename, header, rows: csvRows });
}
